import {
  Stack,
  Text,
  Box,
  Button,
  Popover,
  PopoverContent,
  PopoverBody,
  useColorModeValue,
  Center,
  IconButton,
  Tooltip,
  useBreakpointValue,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import React, { useContext, useEffect, useState } from "react";
import { updateTypedUserPreference } from "state/userPreference/operations";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import OnboardingVideo from "../OnboardingVideo";
import { useButtonProps, useProjectParams, useUserPreference } from "hooks";
import { useCopyValue } from "hooks/useCopies";
import { SettingsProviderContext } from "screens/panels/settings/SettingsProvider";
import { MarkdownViewer } from "screens/markdown/MarkdownViewer";
import type { RootState } from "state/rootReducer";

const FirstStep = ({ onClose, onNext }: { onClose: () => void; onNext: () => void }) => {
  const bgColor = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.300", "gray.800");
  const commonButtonProps = useButtonProps("md", "cta");

  return (
    <PopoverContent
      boxShadow="0 0 15px 4px rgba(66, 153, 225, 0.36),0 10px 15px 0 rgba(0, 0, 0, 0.15)"
      _focus={{ boxShadow: "0 0 15px 4px rgba(66, 153, 225, 0.36),0 10px 15px 0 rgba(0, 0, 0, 0.15)" }}
      width="22rem"
      border="1px solid"
      borderRadius={"lg"}
      borderColor={borderColor}
      bg={bgColor}
      zIndex={10}>
      <Box
        borderLeft={"1px solid"}
        borderTop={"1px solid"}
        borderColor={borderColor}
        height="3rem"
        width="3rem"
        left="9.5rem"
        top="-1.5rem"
        bg={bgColor}
        position={"absolute"}
        transform="rotate(45deg)"
        zIndex={9}
      />
      <PopoverBody px={"1rem"}>
        <Tooltip label="Close the tour" aria-label="Close the tour" placement="top">
          <IconButton
            aria-label="Close popover"
            icon={<CloseIcon />}
            variant={"ghost"}
            _hover={{}}
            size="sm"
            position="absolute"
            right="0.5rem"
            top="0.5rem"
            onClick={onClose}
          />
        </Tooltip>
        <Stack spacing="1.5rem" p="1rem">
          <MarkdownViewer content={useCopyValue("copy_markdown_onboarding_step_one")} fontSize="sm" backgroundColor="transparent" />
          <Center>
            <Button {...commonButtonProps} borderRadius="full" width="12rem" onClick={onNext}>
              Next
            </Button>
          </Center>
        </Stack>
      </PopoverBody>
    </PopoverContent>
  );
};

const SecondStep = ({ onClose }: { onClose: () => void }) => {
  const bgColor = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.300", "gray.800");
  const commonButtonProps = useButtonProps("md", "cta");

  return (
    <PopoverContent
      boxShadow="0 0 15px 4px rgba(66, 153, 225, 0.36),0 10px 15px 0 rgba(0, 0, 0, 0.15)"
      width="22rem"
      border="1px solid"
      borderRadius={"lg"}
      borderColor={borderColor}
      bg={bgColor}
      _focus={{ boxShadow: "0 0 15px 4px rgba(66, 153, 225, 0.36),0 10px 15px 0 rgba(0, 0, 0, 0.15)" }}
      zIndex={10000}>
      <Box
        zIndex={10000}
        borderLeft={"1px solid"}
        borderBottom={"1px solid"}
        borderColor={borderColor}
        boxShadow={"2xl"}
        height="3rem"
        width="3rem"
        left="-1.5rem"
        top="25rem"
        bg={bgColor}
        position={"absolute"}
        transform="rotate(45deg)"
      />
      <PopoverBody px={"1rem"} zIndex={10000}>
        <Tooltip label="Close the tour" aria-label="Close the tour" placement="top">
          <IconButton
            aria-label="Close popover"
            icon={<CloseIcon />}
            variant={"ghost"}
            _hover={{}}
            size="sm"
            position="absolute"
            right="0.5rem"
            top="0.5rem"
            onClick={onClose}
          />
        </Tooltip>
        <MarkdownViewer content={useCopyValue("copy_markdown_onboarding_step_two_heading")} backgroundColor="transparent" />
        <Stack spacing="1rem" px="1rem" pt="0.5rem">
          <Box width="100%">
            <OnboardingVideo maxImageWidth="100%" showCloseButton={false} onCloseVideo={onClose} />
          </Box>
          <MarkdownViewer content={useCopyValue("copy_markdown_onboarding_step_two")} backgroundColor="transparent" />
          <Center mb="1rem">
            <Button {...commonButtonProps} borderRadius="full" width="12rem" onClick={onClose}>
              Close the Tour
            </Button>
          </Center>
        </Stack>
      </PopoverBody>
    </PopoverContent>
  );
};

const ThirdStep = ({ onClose }: { onClose: () => void }) => {
  const bgColor = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.300", "gray.800");
  const commonButtonProps = useButtonProps("md", "cta");
  const onboardingUrl = useCopyValue("copy_onboarding_quadrant_url");

  return (
    <PopoverContent
      boxShadow="0 0 15px 4px rgba(66, 153, 225, 0.36),0 10px 15px 0 rgba(0, 0, 0, 0.15)"
      width="30rem"
      border="1px solid"
      borderRadius={"lg"}
      borderColor={borderColor}
      bg={bgColor}
      _focus={{ boxShadow: "0 0 15px 4px rgba(66, 153, 225, 0.36),0 10px 15px 0 rgba(0, 0, 0, 0.15)" }}
      zIndex={10}>
      <Box
        borderLeft={"1px solid"}
        borderBottom={"1px solid"}
        borderColor={borderColor}
        boxShadow={"2xl"}
        height="3rem"
        width="3rem"
        left="-1.5rem"
        top="10rem"
        bg={bgColor}
        position={"absolute"}
        transform="rotate(45deg)"
      />
      <PopoverBody px={"1rem"}>
        <Tooltip label="Close the tour" aria-label="Close the tour" placement="top">
          <IconButton
            aria-label="Close popover"
            icon={<CloseIcon />}
            variant={"ghost"}
            _hover={{}}
            size="sm"
            position="absolute"
            right="0.5rem"
            top="0.5rem"
            onClick={onClose}
          />
        </Tooltip>
        <Stack spacing="1.5rem" p="1rem">
          <Stack spacing={3}>
            <MarkdownViewer content={useCopyValue("copy_markdown_onboarding_step_three")} fontSize="sm" backgroundColor="transparent" />
            <Text fontSize={"sm"}>
              <a href={onboardingUrl} target="_blank" rel="noopener noreferrer">
                Click here for more information about the Pi Quadrant Index.
              </a>
            </Text>
          </Stack>
          <Center pt="1rem">
            <Button {...commonButtonProps} borderRadius="full" width="9rem" onClick={onClose}>
              Close
            </Button>
          </Center>
        </Stack>
      </PopoverBody>
    </PopoverContent>
  );
};

const OnboardingVideoPopover = () => {
  const { isPortfolios, projectId, parentRoute } = useProjectParams();
  const isMobile = useBreakpointValue({ base: true, md: true, lg: false }, { fallback: "md", ssr: false });
  const hasCompletedOnboardingPhaseOne = useUserPreference("ui_onboarding_completed") as boolean;
  const hasCompletedOnboardingPhaseTwo = useUserPreference("ui_onboarding_quadrant_chart_completed") as boolean;
  const hasCompletedLastStep = useUserPreference("ui_onboarding_completed_last_step") as boolean;
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const { setIsSideNavigationOpen, activeStep, setActiveStep } = useContext(SettingsProviderContext);
  const { isLoggedIn } = useSelector((state: RootState) => state.session, shallowEqual);
  const { isLoading: isUserPreferencesLoading, userPreferences } = useSelector((state: RootState) => state.userPreference, shallowEqual);

  const areOnboardingPreferencesLoaded =
    !isUserPreferencesLoading &&
    isLoggedIn &&
    ("ui_onboarding_completed" in userPreferences ||
      "ui_onboarding_quadrant_chart_completed" in userPreferences ||
      "ui_onboarding_completed_last_step" in userPreferences ||
      // If none of the onboarding preferences exist, we consider them "loaded" as defaults (false)
      Object.keys(userPreferences).length > 0);

  // Fallback timeout with longer duration for edge cases where preferences might not load properly
  const [hasFallbackPreferencesLoaded, setHasFallbackPreferencesLoaded] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setHasFallbackPreferencesLoaded(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, [isLoggedIn]);

  const arePreferencesLoaded = areOnboardingPreferencesLoaded || hasFallbackPreferencesLoaded;

  // Uncomment the following lines if you want to reset user preferences on component mount
  // dispatch(updateTypedUserPreference({ preferenceKey: "ui_hide_onboarding_videos", value: false }));
  // dispatch(updateTypedUserPreference({ preferenceKey: "ui_onboarding_completed", value: false }));
  // dispatch(updateTypedUserPreference({ preferenceKey: "ui_onboarding_completed_last_step", value: false }));
  // dispatch(updateTypedUserPreference({ preferenceKey: "ui_onboarding_quadrant_chart_completed", value: false }));
  // useEffect(() => {
  //   console.log("onboarding state", {
  //     activeStep,
  //     hasCompletedOnboardingPhaseOne,
  //     hasCompletedOnboardingPhaseTwo,
  //     isMobile,
  //     parentRoute,
  //     hasCompletedLastStep,
  //     isPortfolios,
  //     isLoggedIn,
  //     isAnonymous,
  //   });
  // }, [
  //   activeStep,
  //   hasCompletedOnboardingPhaseOne,
  //   hasCompletedOnboardingPhaseTwo,
  //   isMobile,
  //   parentRoute,
  //   hasCompletedLastStep,
  //   isPortfolios,
  //   isLoggedIn,
  //   isAnonymous,
  // ]);

  useEffect(() => {
    // Only proceed if preferences have been loaded
    if (!arePreferencesLoaded) {
      return;
    }
    if (
      activeStep === -1 &&
      !isMobile &&
      hasCompletedOnboardingPhaseOne &&
      (!hasCompletedLastStep || !hasCompletedOnboardingPhaseTwo) &&
      isPortfolios
    ) {
      setActiveStep(2);
      setIsOpen(true);
    } else if (activeStep === 0 && !isMobile && !hasCompletedOnboardingPhaseOne) {
      setIsOpen(true);
    } else if (activeStep === -1 && !isMobile && !hasCompletedOnboardingPhaseOne) {
      setActiveStep(0);
      setIsOpen(true);
    }
  }, [
    isLoggedIn,
    isPortfolios,
    projectId,
    parentRoute,
    hasCompletedOnboardingPhaseOne,
    isMobile,
    setIsSideNavigationOpen,
    hasCompletedLastStep,
    hasCompletedOnboardingPhaseTwo,
    setActiveStep,
    activeStep,
    arePreferencesLoaded,
    isUserPreferencesLoading,
  ]);

  const hasCompletedStepOne = () => {
    setIsSideNavigationOpen(true);
    setActiveStep(1);
  };

  const hasCompletedPhaseOne = () => {
    setIsOpen(false);
    setActiveStep(2);
    dispatch(updateTypedUserPreference({ preferenceKey: "ui_onboarding_completed", value: true }));
  };

  const hasCompletedPhaseTwo = () => {
    setIsOpen(false);
    setActiveStep(-2);
    dispatch(updateTypedUserPreference({ preferenceKey: "ui_onboarding_quadrant_chart_completed", value: true }));
    dispatch(updateTypedUserPreference({ preferenceKey: "ui_onboarding_completed_last_step", value: true }));
  };

  return (
    <>
      {isOpen && <Box position="fixed" top="0" left="0" right="0" bottom="0" bg="blackAlpha.200" zIndex={10} display="block" />}
      <Popover isOpen={isOpen} placement="right-start" arrowSize={40} closeOnEsc={false} closeOnBlur={false}>
        <Box
          zIndex={10000}
          position={"absolute"}
          top={activeStep === 2 ? "7.5rem" : activeStep === 1 ? "unset" : "5.2rem"}
          bottom={activeStep === 1 ? "35rem" : "unset"}
          left={activeStep === 0 ? "38vw" : activeStep === 1 ? "14rem" : "57vw"}>
          {activeStep === 2 ? (
            <ThirdStep onClose={hasCompletedPhaseTwo} />
          ) : activeStep === 0 ? (
            <FirstStep onClose={hasCompletedPhaseOne} onNext={hasCompletedStepOne} />
          ) : activeStep === 1 ? (
            <SecondStep onClose={hasCompletedPhaseOne} />
          ) : null}
        </Box>
      </Popover>
    </>
  );
};

export default OnboardingVideoPopover;
